/**
 * AI Models Catalog API Client
 *
 * Browse admin-enabled AI models for user curation into personal library.
 * Uses apiCall helper for consistent authenticated requests.
 */

import { apiCall } from '@/lib/api/api-helpers';

export interface CatalogModel {
  id: string
  name: string
  endpointId: string
  description?: string
  category: string
  nodeType?: string | null
  modelFamily?: string
  modelFamilyId?: string
  purpose?: string
  featuredScore: number
  capabilities: string[]
  thumbnailUrl?: string
  licenseType?: string
  modelKind?: string
  durationEstimate?: number
  pricing?: {
    originalPrice?: number
    fallbackPrice?: number
    currency?: string
    unit?: string
    pricingSource?: string
  }
  provider?: { id: string; key: string; name: string; enabled: boolean }
}

export interface CatalogModelListResponse {
  data: CatalogModel[]
  meta: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface ModelCatalogQuery {
  search?: string
  nodeType?: string
  category?: string
  modelFamily?: string
  purpose?: string
  purposeNot?: string
  sort?: 'featured' | 'name-asc' | 'name-desc'
  page?: number
  limit?: number
  subPurpose?: string
}

export interface ModelFilterOptions {
  families: string[]
  categories: string[]
  nodeTypes: string[]
  purposes: string[]
}

export const modelsAPI = {
  /**
   * Browse all admin-enabled models — paginated, filterable, sortable.
   */
  async getCatalog(query?: ModelCatalogQuery): Promise<CatalogModelListResponse> {
    const params = new URLSearchParams();
    if (query?.search) params.set('search', query.search);
    if (query?.nodeType) params.set('nodeType', query.nodeType);
    if (query?.category) params.set('category', query.category);
    if (query?.modelFamily) params.set('modelFamily', query.modelFamily);
    if (query?.purpose) params.set('purpose', query.purpose);
    if (query?.purposeNot) params.set('purposeNot', query.purposeNot);
    if (query?.sort) params.set('sort', query.sort);
    if (query?.subPurpose) params.set('subPurpose', query.subPurpose);
    if (query?.page !== undefined) params.set('page', String(query.page));
    if (query?.limit !== undefined) params.set('limit', String(query.limit));
    const endpoint = `/api/model-management${params.toString() ? `?${params}` : ''}`;
    const result = await apiCall(endpoint, true) as { data: CatalogModelListResponse };
    return result.data;
  },

  /**
   * Find a model by endpoint ID (e.g. "fal-ai/video-prompt-generator").
   * Returns the model ID or null if not found.
   */
  async findByEndpointId(endpointId: string): Promise<string | null> {
    const result = await this.getCatalog({ search: endpointId, limit: 1 });
    const match = result.data.find(m => m.endpointId === endpointId);
    return match?.id ?? null;
  },

  /**
   * Get available filter options (families, categories) for enabled models.
   */
  async getFilters(): Promise<ModelFilterOptions> {
    const result = await apiCall('/api/model-management/filters', true) as { data: ModelFilterOptions };
    return result.data;
  },
};
